import React, { useMemo, useRef } from 'react';
import type { BackgroundThemeProps } from '../backgroundThemes';
import { useCanvasLoop } from '../useCanvasLoop';

type Size = { width: number; height: number };

interface Star {
  x: number;
  y: number;
  radius: number;
  speed: number;
  alpha: number;
  phase: number;
  twinkleSpeed: number;
  spriteIndex: number;
}

interface Sprite {
  canvas: HTMLCanvasElement;
  offset: number;
  side: number;
}

const DESKTOP_COUNT = 26;
const MOBILE_COUNT = 14;
const MAX_DELTA_MS = 250;
const TWO_PI = Math.PI * 2;
const STAR_RADII = [4, 5.75, 7.25, 9] as const;
const HALO_SCALE = 2.6;
// Sprites are rasterised at 2x so they stay crisp after the loop's DPR (≤1.5) transform.
const SPRITE_RESOLUTION = 2;

const randomBetween = (min: number, max: number) => min + Math.random() * (max - min);

const drawStarPath = (ctx: CanvasRenderingContext2D, center: number, radius: number) => {
  const innerRadius = radius * 0.45;
  ctx.beginPath();
  for (let point = 0; point < 10; point += 1) {
    const angle = -Math.PI / 2 + (point * Math.PI) / 5;
    const pointRadius = point % 2 === 0 ? radius : innerRadius;
    const x = center + Math.cos(angle) * pointRadius;
    const y = center + Math.sin(angle) * pointRadius;
    if (point === 0) {
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
    }
  }
  ctx.closePath();
};

export const GlowingStarRainBackground: React.FC<BackgroundThemeProps> = ({ tone, activity }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null!);

  const renderer = useMemo(() => {
    let stars: Star[] = [];
    let sprites: Sprite[] = [];
    let elapsedSeconds = 0;
    const isDark = tone === 'dark';
    const fillColor = isDark ? 'rgba(254, 243, 199, 1)' : 'rgba(234, 179, 8, 1)';
    const haloColor = isDark ? 'rgba(250, 204, 21, 0.55)' : 'rgba(202, 138, 4, 0.35)';
    const haloEdge = isDark ? 'rgba(250, 204, 21, 0)' : 'rgba(202, 138, 4, 0)';
    const strokeColor = isDark ? '' : 'rgba(161, 98, 7, 0.6)';

    const createSprite = (radius: number): Sprite => {
      const haloRadius = radius * HALO_SCALE;
      const side = Math.ceil(haloRadius * 2 + 4);
      const center = side / 2;
      const canvas = document.createElement('canvas');
      canvas.width = side * SPRITE_RESOLUTION;
      canvas.height = side * SPRITE_RESOLUTION;
      const ctx = canvas.getContext('2d');

      if (ctx) {
        ctx.scale(SPRITE_RESOLUTION, SPRITE_RESOLUTION);
        const gradient = ctx.createRadialGradient(center, center, 0, center, center, haloRadius);
        gradient.addColorStop(0, haloColor);
        gradient.addColorStop(1, haloEdge);
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(center, center, haloRadius, 0, TWO_PI);
        ctx.fill();
        drawStarPath(ctx, center, radius);
        ctx.fillStyle = fillColor;
        ctx.fill();
        if (strokeColor) {
          ctx.strokeStyle = strokeColor;
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      }

      return { canvas, offset: center, side };
    };

    const resetStar = (star: Star, size: Size, fromTop: boolean) => {
      const spriteIndex = Math.floor(Math.random() * STAR_RADII.length);
      const radius = STAR_RADII[spriteIndex];
      const speedProgress =
        (radius - STAR_RADII[0]) / (STAR_RADII[STAR_RADII.length - 1] - STAR_RADII[0]);
      star.x = randomBetween(radius, Math.max(radius, size.width - radius));
      star.y = fromTop ? -radius * HALO_SCALE : randomBetween(0, size.height);
      star.radius = radius;
      star.speed = 60 + speedProgress * 110;
      star.alpha = randomBetween(0.65, 1);
      star.phase = randomBetween(0, TWO_PI);
      star.twinkleSpeed = randomBetween(1.2, 2.8);
      star.spriteIndex = spriteIndex;
    };

    const createStar = (size: Size): Star => {
      const star = {} as Star;
      resetStar(star, size, false);
      return star;
    };

    const drawStar = (ctx: CanvasRenderingContext2D, star: Star, alpha: number) => {
      const sprite = sprites[star.spriteIndex];
      if (!sprite) return;
      ctx.globalAlpha = alpha;
      ctx.drawImage(
        sprite.canvas,
        star.x - sprite.offset,
        star.y - sprite.offset,
        sprite.side,
        sprite.side
      );
    };

    return {
      setup(ctx: CanvasRenderingContext2D, size: Size) {
        sprites = STAR_RADII.map(createSprite);
        stars = Array.from({ length: size.width >= 768 ? DESKTOP_COUNT : MOBILE_COUNT }, () =>
          createStar(size)
        );
        elapsedSeconds = 0;
        ctx.clearRect(0, 0, size.width, size.height);
      },
      draw(ctx: CanvasRenderingContext2D, size: Size, deltaMs: number) {
        const deltaSeconds = Math.min(deltaMs, MAX_DELTA_MS) / 1000;
        elapsedSeconds += deltaSeconds;
        ctx.globalCompositeOperation = 'destination-out';
        ctx.fillStyle = 'rgba(0,0,0,0.16)';
        ctx.fillRect(0, 0, size.width, size.height);
        ctx.globalCompositeOperation = 'source-over';

        for (let index = 0; index < stars.length; index += 1) {
          const star = stars[index];
          star.y += star.speed * deltaSeconds;
          if (star.y > size.height + star.radius * HALO_SCALE) {
            resetStar(star, size, true);
          }
          const twinkle =
            0.65 +
            ((Math.sin(star.phase + elapsedSeconds * star.twinkleSpeed) + 1) / 2) *
              0.35 *
              star.alpha;
          drawStar(ctx, star, twinkle);
        }
        ctx.globalAlpha = 1;
      },
      drawStatic(ctx: CanvasRenderingContext2D, size: Size) {
        ctx.clearRect(0, 0, size.width, size.height);
        ctx.globalCompositeOperation = 'source-over';
        for (let index = 0; index < stars.length; index += 1) {
          const star = stars[index];
          ctx.globalAlpha = 1;
          const gradient = ctx.createLinearGradient(
            star.x,
            star.y - star.radius * 4,
            star.x,
            star.y
          );
          gradient.addColorStop(0, haloEdge);
          gradient.addColorStop(1, haloColor);
          ctx.strokeStyle = gradient;
          ctx.lineWidth = Math.max(1, star.radius * 0.24);
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.moveTo(star.x, star.y - star.radius * 4);
          ctx.lineTo(star.x, star.y - star.radius * 0.6);
          ctx.stroke();
          drawStar(ctx, star, star.alpha);
        }
        ctx.globalAlpha = 1;
      },
    };
  }, [tone]);

  useCanvasLoop(canvasRef, renderer, activity);

  return <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />;
};
